import { useState, useEffect } from "react";
import "@/App.css";

function App() {
  const [cart, setCart] = useState(() => {
    // Inicializar desde localStorage
    const savedCart = localStorage.getItem("barranco-cart");
    return savedCart ? JSON.parse(savedCart) : [];
  });
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    address: "",
    reference: "",
    paymentMethod: "efectivo"
  });

  // Guardar carrito en localStorage cuando cambie
  useEffect(() => {
    localStorage.setItem("barranco-cart", JSON.stringify(cart));
  }, [cart]);

  // Scroll spy para el menú de navegación
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['bocadillos', 'costillas', 'pizzas', 'pastas', 'combos', 'bebidas'];
      const scrollPosition = window.scrollY + 200;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const addToCart = (product) => {
    const existingIndex = cart.findIndex(
      item => item.id === product.id && 
              JSON.stringify(item.variant) === JSON.stringify(product.variant)
    );

    if (existingIndex >= 0) {
      const newCart = [...cart];
      newCart[existingIndex].quantity += 1;
      setCart(newCart);
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
  };

  const removeFromCart = (index) => {
    const newCart = cart.filter((_, i) => i !== index);
    setCart(newCart);
  };

  const updateQuantity = (index, change) => {
    const newCart = [...cart];
    newCart[index].quantity += change;
    if (newCart[index].quantity <= 0) {
      newCart.splice(index, 1);
    }
    setCart(newCart);
  };

  const calculateTotal = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0).toFixed(2);
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      alert("Tu carrito está vacío");
      return;
    }
    setShowCheckout(true);
    setShowCart(false);
  };

  const sendToWhatsApp = () => {
    if (!formData.name || !formData.phone || !formData.address) {
      alert("Por favor completa todos los campos obligatorios");
      return;
    }

    let message = `🌲 *PEDIDO - EL BARRANCO MÁGICO* 🌲\n\n`;
    message += `*Cliente:* ${formData.name}\n`;
    message += `*Teléfono:* ${formData.phone}\n`;
    message += `*Dirección:* ${formData.address}\n`;
    if (formData.reference) {
      message += `*Referencia:* ${formData.reference}\n`;
    }
    message += `*Método de pago:* ${formData.paymentMethod === "efectivo" ? "Efectivo" : "QR Bancario"}\n\n`;
    
    message += `*DETALLE DEL PEDIDO:*\n`;
    cart.forEach((item, index) => {
      message += `\n${index + 1}. ${item.name}`;
      if (item.variant) {
        message += ` (${item.variant})`;
      }
      message += `\n   Cantidad: ${item.quantity}\n`;
      message += `   Precio: $${(item.price * item.quantity).toFixed(2)}\n`;
    });

    message += `\n*TOTAL: $${calculateTotal()}*\n\n`;
    message += `⏳ *Tiempo estimado: 45-60 minutos*\n\n`;
    
    if (formData.paymentMethod === "qr") {
      message += `💳 *Realizaré el pago por QR y enviaré el comprobante*`;
    }

    const whatsappNumber = "593993159551"; // Nuevo número actualizado
    const encodedMessage = encodeURIComponent(message);
    const whatsappURL = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
    
    window.open(whatsappURL, "_blank");
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="App">
      {/* Hero Section */}
      <section className="hero-section" data-testid="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className="logo-container" data-testid="logo-container">
            <img 
              src="https://i.postimg.cc/Yq4FGj1t/logo2_0.png" 
              alt="El Barranco Mágico y Encantador"
              className="hero-logo"
            />
          </div>
          <h1 className="hero-title" data-testid="restaurant-name">El Barranco Mágico y Encantador</h1>
          <p className="hero-slogan" data-testid="slogan">Sabores del Bosque, Hechos a Mano</p>
          <p className="hero-description">
            Bienvenido, viajero. Has encontrado un refugio donde la tradición artesanal 
            se encuentra con la magia del bosque. Cada plato es una historia, 
            cada bocado un hechizo de sabores auténticos.
          </p>
          <button 
            className="cta-button" 
            data-testid="order-cta-button"
            onClick={() => scrollToSection('menu')}
          >
            Invocar Pedido a Domicilio
          </button>
        </div>
      </section>

      {/* Menú de navegación sticky */}
      <nav className="menu-nav" data-testid="menu-nav">
        <div className="menu-nav-container">
          <button 
            className={`menu-nav-item ${activeSection === 'bocadillos' ? 'active' : ''}`}
            onClick={() => scrollToSection('bocadillos')}
            data-testid="nav-bocadillos"
          >
            Bocadillos
          </button>
          <button 
            className={`menu-nav-item ${activeSection === 'costillas' ? 'active' : ''}`}
            onClick={() => scrollToSection('costillas')}
            data-testid="nav-costillas"
          >
            Costillas
          </button>
          <button 
            className={`menu-nav-item ${activeSection === 'pizzas' ? 'active' : ''}`}
            onClick={() => scrollToSection('pizzas')}
            data-testid="nav-pizzas"
          >
            Pizzas
          </button>
          <button 
            className={`menu-nav-item ${activeSection === 'pastas' ? 'active' : ''}`}
            onClick={() => scrollToSection('pastas')}
            data-testid="nav-pastas"
          >
            Pastas
          </button>
          <button 
            className={`menu-nav-item ${activeSection === 'combos' ? 'active' : ''}`}
            onClick={() => scrollToSection('combos')}
            data-testid="nav-combos"
          >
            Combos
          </button>
          <button 
            className={`menu-nav-item ${activeSection === 'bebidas' ? 'active' : ''}`}
            onClick={() => scrollToSection('bebidas')}
            data-testid="nav-bebidas"
          >
            Bebidas
          </button>
        </div>
      </nav>

      {/* Menú */}
      <section className="menu-section" id="menu" data-testid="menu-section">
        <h2 className="section-title">Nuestro Menú Encantado</h2>

        {/* Bocadillos del Sendero */}
        <div id="bocadillos">
          <MenuCategory 
            title="Bocadillos del Sendero"
            subtitle="Pequeños tesoros para comenzar la aventura"
            items={[
              {
                id: "humitas",
                name: "Humitas del Abuelo Duende",
                price: 1.25,
                description: "Un secreto de maíz envuelto en hojas, queso derretido, como el fogón de una cabaña escondida.",
                image:"https://i.postimg.cc/SRX2jfFb/humita.png"
              },
              {
                id: "bunuelos",
                name: "Buñuelos de la Hada Harinera",
                price: 1.50,
                description: "Burbujas doradas y esponjosas, espolvoreadas con azúcar que brilla como el rocío del amanecer y un toque de miel.",
                image: "https://i.postimg.cc/WzDFtwRB/bunnuelos.png"
              },
              {
                id: "panYuca",
                name: "Panecillos de Yuca del Bosque",
                price: 0.50,
                description: "Crujientes por fuera, suaves por dentro. Perfectos para acompañar.",
                image: "https://i.postimg.cc/q73ptDgm/panes-Yuca.png"
              }
            ]}
            addToCart={addToCart}
          />
        </div>

        {/* Costillas Ahumadas */}
        <div id="costillas">
          <MenuCategory 
            title="Costillas Ahumadas"
            subtitle="Un ritual de humo y paciencia"
            items={[
              {
                id: "rack-completo",
                name: "Rack Completo del Banquete del Guardabosques",
                price: 13.99,
                description: "Para compartir y celebrar(Para 3 personas). Nuestro tesoro ahumado, glaseado con la salsa secreta del barranco. Acompañado del jardín de papas chauchas al romero y la fresca ensalada de la casa",
                image:"https://i.postimg.cc/Y0J4FLM9/rack_Completo.png",
                hasVariants: true,
                variants: [
                  { label: "Normal (incluida)", price: 0 },
                  { label: "Extra salsa", price: 0.50 },
                  { label: "Salsa por separado", price: 0 }
                ]
              },
              {
                id: "medio-rack",
                name: "½ Rack del Explorador Solitario",
                price: 4.99,
                description: " La porción perfecta para una aventura en solitario. La misma magia jugosa y deshuesable, con sus fieles compañeros: papas chauchas y ensalada.",
                image:"https://i.postimg.cc/zv9LhH8V/rack_Medio.png",
                hasVariants: true,
                variants: [
                  { label: "Normal (incluida)", price: 0 },
                  { label: "Extra salsa", price: 0.50 },
                  { label: "Salsa por separado", price: 0 }
                ]
              }
            ]}
            addToCart={addToCart}
          />
        </div>

        {/* Pizzas Artesanales */}
        <div id="pizzas">
          <MenuCategory 
            title="Pizzas Artesanales"
            subtitle="Masa madre fermentada lentamente"
            items={[
              {
                id: "pizza-barranco",
                name: "Pizza 'El Barranco'",
                price: 7.99,
                description: "La reunión de los sabores del bosque en un solo lugar. Salsa rúgu, mozzarella fundida, jamón, salami, tocino crujiente y dulces choclos. Incluye una Ensalada de la Casa (del tamaño personal).",
                image:"https://i.postimg.cc/Zn9B0xtY/pizza_Barranco.png",
                hasVariants: true,
                variants: [
                  { label: "Fogón (4 pedazos)", price: 0 },
                  { label: "Horno del Bosque (6-7 pedazos)", price: 4.00 },
                  { label: "Gran horno ancestral (8-9 pedazos)", price: 7.00 }
                ]
              }
            ]}
            addToCart={addToCart}
          />
        </div>

        {/* Pastas Caseras */}
        <div id="pastas">
          <MenuCategory 
            title="Pastas Caseras"
            subtitle="Hechas en casa con amor y tradición"
            items={[
              {
                id: "lasagna",
                name: "Lasaña Tradicional",
                price: 6.99,
                description: "El clásico reconfortante del bosque. Capas de pasta, nuestro ragú de larga cocción y bechamel, horneadas hasta gratinar. Un tesoro en cada porción.",
                image:"https://i.postimg.cc/7hCGbn8L/lasana.png"
              },
              {
                id: "bolognesa",
                name: "Pasta Bolognesa",
                price: 5.99,
                description: "El abrazo sustancioso. Nuestra salsa de carne lentamente guisada, coronando un nido de tagliatelle fresco. Sabor profundo y casero.",
                image:"https://i.postimg.cc/Fzfd1V5s/pasta_Bolonesa.png"
              },
              {
                id: "alfredo",
                name: "Pasta Alfredo",
                price: 5.99,
                description: "Cremoso y elegante. Fettuccine bañado en una salsa suave de queso parmesano y mantequilla. Un hechizo de sencillez deliciosa.",
                image:"https://i.postimg.cc/2yqb37Rz/pasta_Alfredo.png"
              },
              {
                id: "carbonara",
                name: "Pasta Carbonara",
                price: 5.99,
                description: "Auténtica y seductora. Espagueti fresco mezclado con yema dorada, queso pecorino y pimienta negra, salteado con trozos de panceta crujiente. Pura magia romana.",
                image:"https://i.postimg.cc/432NWpbs/carbonara1.png"
              }
            ]}
            addToCart={addToCart}
          />
        </div>

        {/* Combos */}
        <div id="combos">
          <MenuCategory 
            title="Combos"
            subtitle="Banquetes preparados para compartir"
            items={[
              {
                id: "duo",
                name: "Duo Encantado (2 personas)",
                price: 15.50,
                description: "Un pacto de sabor para dos almas aventureras. Elige dos pastas de nuestro bosque para compartir, acompañadas de dos limonadas que refrescará tu camino.",
                image:"https://i.postimg.cc/Bb8LtBWd/combo_Duo_Encantado.png"
              },
              {
                id: "explorador",
                name: "Combo del Explorador (1 persona)",
                price: 7.50,
                description: "El festín perfecto para reponer fuerzas en solitario tras recorrer los senderos. Incluye ½ Rack de Costillas Ahumadas, acompañado con una limonada a elección y una porción de buñuelos para endulzar el alma.",
                image:"https://i.postimg.cc/jd1HtCfc/combo_Explorador.png"
              },
              {
                id: "solitario",
                name: "Combo del Duende Solitario (1 persona)",
                price: 7.50,
                description: "Un pequeño festín para quienes recorren el bosque a su propio ritmo. Incluye una pasta a elección de nuestro sendero y una limonada a elección, fresca y revitalizante.",
                image:"https://i.postimg.cc/5NrBbjz3/combo_Duende.png"
              },
              {
                id: "fogata",
                name: "Combo de la Fogata (2-3 personas)",
                price: 20.99,
                description: "Para compartir historias y sabores alrededor de una mesa. Incluye una pizza mediana ‘El Barranco’, una lasaña , ración de humitas del Abuelo Duende(2 unidades) y una jarra de limonada a elección.",
                image:"https://i.postimg.cc/BQzT4tFM/combo_Fogata.png"
              },
              {
                id: "compartir",
                name: "Combo Especial para Compartir",
                price: 26.99,
                description: "Un hechizo pensado para disfrutarse en compañía. Incluye una pasta a elección de nuestro bosque, una pizza grande recién salida del horno de piedra y una jarra de limonada a elección, perfecta para brindar y compartir la aventura.",
                image:"https://i.postimg.cc/fTPcDkdv/combo_Compartir1.png"
              },
              {
                id: "guardian",
                name: "Combo Familiar del Guardián (4 personas)",
                price: 29.99,
                description: "El festín que reúne a la tribu. Un legendario Rack Completo de Costillas Ahumadas, una Pizza Familiar (incluye ensalade de la casa) para coronar la mesa y una Jarra de Limonada a elección para brindar por la aventura.",
                image:"https://i.postimg.cc/7YR3wbzB/combo_Guardian.png"
              },
              {
                id: "panYogurt",
                name: "Ración del Duende del Bosque",
                price: 3.99,
                description: "Una combinación sencilla y encantadora. Incluye una porción de panes de yuca recién horneados, acompañados de yogurt artesanal del bosque, suave y refrescante.",
                image:"https://i.postimg.cc/X7Pf4X5H/combo_Yuca.png"
                
              }
            ]}
            addToCart={addToCart}
          />
        </div>

        {/* Punto de Hidratación */}
        <div id="bebidas">
          <h3 className="category-title">Punto de Hidratación</h3>
          <p className="category-subtitle">Elixires y brebajes para recuperar el aliento tras la aventura</p>
          
          {/* BEBIDAS CALIENTES */}
          <div className="drinks-subsection">
            <h4 className="drinks-subsection-title">☕ Bebidas Calientes</h4>
            <p className="drinks-subsection-subtitle">Reconfortantes brebajes para calentar el alma</p>
            <div className="menu-items">
              {[
                {
                  id: "te-bosque",
                  name: "Té del Bosque",
                  price: 1.50,
                  description: "La infusión reconfortante que preparan las hadas con hierbas del sendero.",
                  image: "https://i.postimg.cc/m2VfYyYc/Te_Casa.jpg"
                },
                {
                  id: "te-flor-barranco",
                  name: "Té de la Flor del Barranco",
                  price: 1.99, 
                  description: "Té de Manzana en Flor. Cálido y aromático, que guarda la magia y el susurro del barranco.", // COMPLETAR DESCRIPCIÓN
                  image: "https://i.postimg.cc/fT5ncfcS/Te_Flor.jpg" 
                },
                {
                  id: "cafe-americano",
                  name: "Café Americano del Guardabosques",
                  price: 1.50,
                  description: "Un café robusto y puro, como el que se toma al borde de la cascada al amanecer.",
                  image:"https://i.postimg.cc/8cgdzYt7/Cafe_Americano.jpg"
                },
                {
                  id: "capuchino",
                  name: "Capuchino del Gnomo",
                  price: 2.00,
                  description: "La mezcla perfecta de espresso intenso y espuma suave, con un toque de magia en cada sorbo.",
                  image: "https://i.postimg.cc/ncTb4G4v/CAPUCHINO.jpg"
                },
                {
                  id: "mocachino",
                  name: "Mocachino Encantado",
                  price: 2.00,
                  description: "Para los amantes del dulce. Chocolate, café y leche se unen en un hechizo irresistible.",
                  image: "https://i.postimg.cc/jdv0H4Hy/MOCACHINO.jpg"
                },
                {
                  id: "chocolate",
                  name: "Chocolate de la Hada Invernal",
                  price: 2.00,
                  description: "Espeso, cremoso y reconfortante. El abrigo perfecto para las tardes frescas del barranco.",
                  image:"https://i.postimg.cc/GtnPmfq8/Chocolate_Caliente.jpg"
                },
                {
                  id: "matcha-caliente",
                  name: "Aliento Verde",
                  price: 2.50, 
                  description: "Matcha caliente que reconforta el alma, calma el espíritu y envuelve el cuerpo en serenidad verde.", 
                  image: "https://i.postimg.cc/T3nz2yGD/Matcha-Caliente.jpg" 
                }
              ].map((item) => (
                <MenuItemCard key={item.id} item={item} addToCart={addToCart} />
              ))}
            </div>
          </div>

          {/* BEBIDAS FRÍAS */}
          <div className="drinks-subsection">
            <h4 className="drinks-subsection-title">🧊 Bebidas Frías</h4>
            <p className="drinks-subsection-subtitle">Refrescantes elixires para los días soleados</p>
            <div className="menu-items">
              {[
                {
                  id: "limonada-clasica",
                  name: "Limonada Cremosa del Arroyuelo",
                  price: 2.50,
                  description: "Nuestra clásica y refrescante limonada, cremosa y con el balance perfecto entre dulce y ácido.",
                  image: "https://i.postimg.cc/DyQ9NWMq/limonada_Cremosa.png",
                  hasVariants: true,
                  variants: [
                    { label: "Personal", price: 0 },
                    { label: "Jarra", price: 4.49 }
                  ]
                },
                {
                  id: "limonada-frutos",
                  name: "Limonada de Frutos Rojos del Matorral",
                  price: 2.50,
                  description: "Una explosión de bayas silvestres en cada sorbo. Dulce, vibrante y llena de vida.",
                  image: "https://i.postimg.cc/7Y1FRCW3/limonada_Frutos_Rojos.png",
                  hasVariants: true,
                  variants: [
                    { label: "Personal", price: 0 },
                    { label: "Jarra", price: 4.49 }
                  ]
                },
                {
                  id: "ice-coffee-original",
                  name: "Sombra del Barranco",
                  price: 2.50, 
                  description: "Café frío en su forma más pura. Intenso, refrescante y equilibrado, ideal para despertar los sentidos como la brisa fresca del barranco.",
                  image: "https://i.postimg.cc/k5gRSmWg/ice_Coffe_O.png" 
                },
                {
                  id: "ice-coffee-blue",
                  name: "Eclipse Azul",
                  price: 2.99, 
                  description: "Café frío con un giro inesperado. Suave, refrescante y misterioso, teñido de azul como las sombras del barranco al caer la noche. Un hechizo helado que despierta los sentidos.", // COMPLETAR DESCRIPCIÓN
                  image: "https://i.postimg.cc/q7v6KT27/ice_Blue.png" 
                },
                {
                  id: "ice-coffee-caramelo",
                  name: "Dulce Hechizo",
                  price: 2.99, 
                  description: "Café frío suavemente endulzado con caramelo dorado. Cremoso, refrescante y envolvente, como un hechizo dulce que se derrite lentamente en el barranco.", 
                  image: "https://i.postimg.cc/R0ZJtBwF/ice_Caramel.png" 
                },
                {
                  id: "ice-matcha-original",
                  name: "Esmeralda del Barranco",
                  price: 2.50, 
                  description: "Matcha frío en su forma más pura. Suave, equilibrado y refrescante, con notas verdes que calman el espíritu y despiertan los sentidos.", 
                  image: "https://i.postimg.cc/0NyKSs76/ice_Matcha.png" 
                },
                {
                  id: "ice-matcha-frutilla",
                  name: "Hechizo Rosado",
                  price: 2.99, 
                  description: "Matcha frío mezclado con frutilla fresca. Dulce, cremoso y encantador, como un sorbo rosado que florece en medio del bosque.", 
                  image: "https://i.postimg.cc/X7KRPZzK/ice_Matcha_Frutilla.png" // 
                },
                {
                  id: "ice-matcha-espresso",
                  name: "Encuentro de Dos Mundos",
                  price: 2.99, 
                  description: "La serenidad del matcha frío se encuentra con la intensidad del espresso. Refrescante, equilibrado y vibrante, un choque de energías que despierta cuerpo y mente.", 
                  image: "https://i.postimg.cc/yYhMGJb0/ice_Matcha_Expresso.png" 
                },
                {
                  id: "fogato",
                  name: "Fogato del Barranco",
                  price: 3.50, 
                  description: "Café frío servido con una bola de helado cremoso que se funde lentamente. Un contraste mágico entre frío y dulzura, perfecto para cerrar el día junto al fogón.", 
                  image: "https://i.postimg.cc/1z3VFQDX/fogato.png"
                },
                {
                  id: "yogurt",
                  name: "Yogurt artesanal del bosque",
                  price: 1.99, 
                  description: "Yogurt suave y cremoso, elaborado de forma artesanal. Fresco, ligero y natural, como un descanso dulce entre senderos del bosque.", 
                  image: "https://i.postimg.cc/BnqrfqrP/yogurt.png"
                }
              ].map((item) => (
                <MenuItemCard key={item.id} item={item} addToCart={addToCart} />
              ))}
            </div>
          </div>

          {/* BEBIDAS CON ALCOHOL - SOLO EN LOCAL */}
          <div className="drinks-subsection alcohol">
            <h4 className="drinks-subsection-title">🍷 Bebidas con Alcohol</h4>
            <p className="drinks-subsection-subtitle">Brebajes espirituosos • Disponibles únicamente en el local</p>
            <div className="menu-items">
              {[
                {
                  id: "hervido-vino",
                  name: "Hervido de Vino del Fogón Ancestral",
                  price: 3.00,
                  description: "Vino especiado y caliente, aromático con canela y clavo. El brebaje que calienta el alma en torno al fuego.",
                  image: "https://i.postimg.cc/HxB25gZr/vino_Hervido.png"
                },
                {
                  id: "jarra-vino",
                  name: "Jarra de Vino del Fogón Ancestral",
                  price: 10.75,
                  description: "Una jarra completa del vino especiado para compartir alrededor de la fogata.",
                  image: "https://i.postimg.cc/QMHfggx0/jarra_Hervido.png"
                },
                {
                  id: "mojito-clasico",
                  name: "Brisa del Barranco",
                  price: 4.50, 
                  description: "Hierbabuena fresca, limón y burbujas que despiertan los sentidos. Refrescante y equilibrado, como una brisa que recorre el barranco.", 
                  image: "https://i.postimg.cc/fbpB83Jf/mojito_Clasico.png" 
                },
                {
                  id: "mojito-frutos-rojos",
                  name: "Encanto Carmesí",
                  price: 4.99, 
                  description: "Frutos rojos macerados, menta y un toque cítrico en perfecta armonía. Dulce, vibrante y refrescante, un hechizo carmesí para brindar.", 
                  image: "https://i.postimg.cc/CKXmcZz6/mojitos_Frutos_Rojos.png" 
                },
                {
                  id: "hechizo-10",
                  name: "El Hechizo del 10",
                  price: 2.99, 
                  description: "Vino tinto servido bien frío, suave y refrescante. Ideal para disfrutar en solitario y brindar con calma.", 
                  image: "https://i.postimg.cc/2SVPSH5S/hechizo10.png" 
                },
                {
                  id: "jarra-hechizo-10",
                  name: "Jarra del Hechizo del 10",
                  price: 12.99, 
                  description: "Vino tinto bien frío para compartir. Refrescante, ligero y perfecto para acompañar largas conversaciones y risas alrededor de la mesa.", 
                  image: "https://i.postimg.cc/V6DRM8GS/jarra_Hechizo10.png" 
                },
                {
                  id: "shots-tequila",
                  name: "Ritual del Barranco",
                  price: 11.99, 
                  description: "Cinco shots servidos con limón y sal. Intensos y directos, pensados para compartir, celebrar y encender el espíritu del barranco.", 
                  image: "https://i.postimg.cc/1z2Jvgf2/shots_Tequila.png" 
                },
                {
                  id: "cerveza-club",
                  name: "Club Verde - Espíritu del Sendero",
                  price: 1.99 , 
                  description: "Ligera y refrescante, con carácter suave y equilibrado. Perfecta para acompañar la aventura sin perder el rumbo(Personal 330ml).", 
                  image: "https://i.postimg.cc/PJVxD8Xy/club1.png" 
                },
                {
                  id: "cerveza-corona",
                  name: "Corona Extra - Brisa Dorada del Barranco",
                  price: 2.99 , 
                  description: "Clara, fresca y ligera. Servida bien fría, ideal para disfrutar con una rodaja de limón y dejar que fluya la conversación(Personal 330ml).", 
                  image: "https://i.postimg.cc/qqDRKCJt/corona1.png" 
                },
                {
                  id: "cerveza-heineken",
                  name: "Heineken - Guardián Esmeralda",
                  price: 2.25 , 
                  description: "Cerveza premium de sabor equilibrado y final ligeramente amargo. Firme, elegante y con carácter propio(Personal 330ml).", 
                  image: "https://i.postimg.cc/L5QXPZ93/heineken1.png" 
                },
                {
                  id: "cerveza-stella",
                  name: "Stella Artois - Estrella del Fogón",
                  price: 2.99 , 
                  description: "Refinada y suave, con notas ligeramente maltosas y final limpio. Una elección distinguida para brindar bajo las estrellas(Personal 330ml).", 
                  image: "https://i.postimg.cc/wTRJkWDZ/stella.png" 
                },

              ].map((item) => (
                <MenuItemCard key={item.id} item={item} localOnly={true} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Carrito flotante */}
      <button 
        className="cart-floating-button" 
        data-testid="cart-button"
        onClick={() => setShowCart(!showCart)}
      >
        🛒 ({cart.reduce((sum, item) => sum + item.quantity, 0)})
      </button>

      {/* Modal del carrito */}
      {showCart && (
        <div className="cart-modal" data-testid="cart-modal" onClick={() => setShowCart(false)}>
          <div className="cart-content" onClick={(e) => e.stopPropagation()}>
            <div className="cart-header">
              <h3>Tu Carrito Mágico</h3>
              <button onClick={() => setShowCart(false)} data-testid="close-cart-button" className="close-btn-desktop">✕</button>
            </div>
            {/* Botón cancelar visible en móvil */}
            <button 
              className="cancel-button-mobile" 
              onClick={() => setShowCart(false)}
              data-testid="cancel-cart-mobile"
            >
              ← Volver al Menú
            </button>
            <div className="cart-items">
              {cart.length === 0 ? (
                <p className="empty-cart">Tu carrito está vacío</p>
              ) : (
                cart.map((item, index) => (
                  <div key={index} className="cart-item" data-testid={`cart-item-${index}`}>
                    <div className="cart-item-info">
                      <h4>{item.name}</h4>
                      {item.variant && <p className="variant">{item.variant}</p>}
                      <p className="price">${item.price.toFixed(2)}</p>
                    </div>
                    <div className="cart-item-controls">
                      <button onClick={() => updateQuantity(index, -1)} data-testid={`decrease-qty-${index}`}>-</button>
                      <span data-testid={`item-quantity-${index}`}>{item.quantity}</span>
                      <button onClick={() => updateQuantity(index, 1)} data-testid={`increase-qty-${index}`}>+</button>
                      <button 
                        className="remove-btn" 
                        onClick={() => removeFromCart(index)}
                        data-testid={`remove-item-${index}`}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="cart-footer">
              <div className="cart-total" data-testid="cart-total">
                <strong>Total:</strong> ${calculateTotal()}
              </div>
              <button 
                className="checkout-button" 
                onClick={handleCheckout}
                disabled={cart.length === 0}
                data-testid="checkout-button"
              >
                Continuar al Pedido
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de checkout */}
      {showCheckout && (
        <div className="checkout-modal" data-testid="checkout-modal" onClick={() => setShowCheckout(false)}>
          <div className="checkout-content" onClick={(e) => e.stopPropagation()}>
            <div className="checkout-header">
              <h3>Completar Pedido</h3>
              <button onClick={() => setShowCheckout(false)} data-testid="close-checkout-button" className="close-btn-desktop">✕</button>
            </div>
            {/* Botón cancelar visible en móvil */}
            <button 
              className="cancel-button-mobile" 
              onClick={() => setShowCheckout(false)}
              data-testid="cancel-checkout-mobile"
            >
              ← Volver al Carrito
            </button>

            <div className="delivery-info-box">
              <p className="phone-confirmation">
                📞 <strong>Tu pedido será confirmado mediante una llamada telefónica.</strong><br/>
                Si no logramos comunicarnos contigo, el pedido no será procesado.
              </p>
              <p className="estimated-time">
                ⏳ Tiempo estimado de preparación y entrega: <strong>45–60 minutos</strong>
              </p>
            </div>

            <form className="checkout-form" onSubmit={(e) => { e.preventDefault(); sendToWhatsApp(); }}>
              <div className="form-group">
                <label>Nombre completo *</label>
                <input 
                  type="text" 
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  data-testid="name-input"
                />
              </div>

              <div className="form-group">
                <label>Teléfono *</label>
                <input 
                  type="tel" 
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  required
                  data-testid="phone-input"
                />
              </div>

              <div className="form-group">
                <label>Dirección de entrega *</label>
                <input 
                  type="text" 
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  required
                  data-testid="address-input"
                />
              </div>

              <div className="form-group">
                <label>Referencia (opcional)</label>
                <input 
                  type="text" 
                  value={formData.reference}
                  onChange={(e) => setFormData({...formData, reference: e.target.value})}
                  data-testid="reference-input"
                />
              </div>

              <div className="form-group">
                <label>Método de pago *</label>
                <select 
                  value={formData.paymentMethod}
                  onChange={(e) => setFormData({...formData, paymentMethod: e.target.value})}
                  data-testid="payment-method-select"
                >
                  <option value="efectivo">Efectivo al momento de la entrega</option>
                  <option value="qr">Pago mediante código QR bancario</option>
                </select>
              </div>

              {formData.paymentMethod === "qr" && (
                <div className="qr-info">
                  <div className="qr-image-container" data-testid="qr-image-container">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_bosqueencantado/artifacts/bmgc1vlj_QRcodeDEUNA.jpeg" 
                      alt="QR de pago"
                      className="qr-image"
                    />
                  </div>
                  <p>Si realizas el pago por QR, envía el comprobante por WhatsApp.</p>
                </div>
              )}

              <div className="order-summary">
                <h4>Resumen del pedido</h4>
                {cart.map((item, index) => (
                  <div key={index} className="summary-item">
                    <span>{item.name} {item.variant ? `(${item.variant})` : ""} x{item.quantity}</span>
                    <span>${(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
                <div className="summary-total">
                  <strong>Total:</strong>
                  <strong>${calculateTotal()}</strong>
                </div>
              </div>

              <button type="submit" className="whatsapp-button" data-testid="send-whatsapp-button">
                Enviar Pedido al Barranco por WhatsApp
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>Horarios</h4>
            <p>Miércoles a Domingo</p>
            <p>5:00 p.m. – 10:00 p.m.</p>
          </div>
          <div className="footer-section">
            <h4>Contacto</h4>
            <p>WhatsApp: 099 315 9551</p>
          </div>
          <div className="footer-section">
            <p className="footer-tagline">Donde cada plato cuenta una historia del bosque</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Componente para categorías del menú
function MenuCategory({ title, subtitle, items, addToCart }) {
  const [selectedVariants, setSelectedVariants] = useState({});
  const [flippedItems, setFlippedItems] = useState({});

  const toggleFlip = (itemId, e) => {
    // Evitar el flip si se hace clic en el botón o select
    if (e.target.tagName === 'BUTTON' || e.target.tagName === 'SELECT') return;
    
    setFlippedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const handleAddToCart = (item, e) => {
    e.stopPropagation(); // Evitar que el clic también gire la tarjeta
    
    let finalPrice = item.price;
    let variantLabel = null;

    if (item.hasVariants && selectedVariants[item.id] !== undefined) {
      const variant = item.variants[selectedVariants[item.id]];
      finalPrice += variant.price;
      variantLabel = variant.label;
    }

    addToCart({
      id: item.id,
      name: item.name,
      price: finalPrice,
      variant: variantLabel
    });

    // Reset variant selection y volver a mostrar el frente
    if (item.hasVariants) {
      setSelectedVariants({...selectedVariants, [item.id]: 0});
    }
  };

  return (
    <div className="menu-category" data-testid={`category-${title.toLowerCase().replace(/ /g, '-')}`}>
      <h3 className="category-title">{title}</h3>
      <p className="category-subtitle">{subtitle}</p>
      <div className="menu-items">
        {items.map((item) => (
          <div 
            key={item.id} 
            className={`flip-card ${flippedItems[item.id] ? 'flipped' : ''}`}
            data-testid={`menu-item-${item.id}`}
          >
            <div className="flip-card-inner" onClick={(e) => toggleFlip(item.id, e)}>
              {/* FRENTE DE LA TARJETA */}
              <div className="flip-card-front">
                {/* 
                  PARA AGREGAR FOTOS:
                  1. Agrega el campo "image" al producto en la lista de items arriba
                  2. Ejemplo: { id: "humitas", name: "...", image: "https://tu-url.com/foto.jpg", ... }
                  3. La imagen aparecerá automáticamente en lugar del placeholder
                */}
                {item.image ? (
                  <img src={item.image} alt={item.name} className="item-image" data-testid={`image-${item.id}`} />
                ) : (
                  <div className="item-image-placeholder" data-testid={`image-placeholder-${item.id}`}>
                    📷 Toca para ver descripción
                  </div>
                )}
                <div className="item-front-info">
                  <h4 className="item-name">{item.name}</h4>
                  <span className="item-price-preview" data-testid={`price-${item.id}`}>
                    ${item.price.toFixed(2)}
                  </span>
                </div>
              </div>

              {/* REVERSO DE LA TARJETA (para descripción) */}
              <div className="flip-card-back" data-testid={`item-back-${item.id}`}>
                <div className="back-content">
                  <h4 className="item-name-back">{item.name}</h4>
                  <p className="item-description">{item.description}</p>
                  
                  {item.hasVariants && (
                    <div className="item-variants" onClick={(e) => e.stopPropagation()}>
                      <label>Selecciona:</label>
                      <select 
                        value={selectedVariants[item.id] || 0}
                        onChange={(e) => {
                          e.stopPropagation();
                          setSelectedVariants({
                            ...selectedVariants, 
                            [item.id]: parseInt(e.target.value)
                          });
                        }}
                        data-testid={`variant-select-${item.id}`}
                      >
                        {item.variants.map((variant, index) => (
                          <option key={index} value={index}>
                            {variant.label} {variant.price > 0 ? `(+$${variant.price.toFixed(2)})` : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="item-footer-back">
                    <span className="item-price">
                      ${item.hasVariants && selectedVariants[item.id] !== undefined
                        ? (item.price + item.variants[selectedVariants[item.id]].price).toFixed(2)
                        : item.price.toFixed(2)
                      }
                    </span>
                    <button 
                      className="add-to-cart-button" 
                      onClick={(e) => handleAddToCart(item, e)}
                      data-testid={`add-to-cart-${item.id}`}
                    >
                      Añadir al Carrito
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Componente individual para items de bebidas (con soporte para "solo en local")
function MenuItemCard({ item, addToCart, localOnly = false }) {
  const [isFlipped, setIsFlipped] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(0);

  const toggleFlip = (e) => {
    if (e.target.tagName === 'BUTTON' || e.target.tagName === 'SELECT') return;
    setIsFlipped(!isFlipped);
  };

  const handleAddToCart = (e) => {
    e.stopPropagation();
    
    let finalPrice = item.price;
    let variantLabel = null;

    if (item.hasVariants && selectedVariant !== undefined) {
      const variant = item.variants[selectedVariant];
      finalPrice += variant.price;
      variantLabel = variant.label;
    }

    addToCart({
      id: item.id,
      name: item.name,
      price: finalPrice,
      variant: variantLabel
    });

    if (item.hasVariants) {
      setSelectedVariant(0);
    }
  };

  return (
    <div 
      className={`flip-card ${isFlipped ? 'flipped' : ''}`}
      data-testid={`menu-item-${item.id}`}
    >
      <div className="flip-card-inner" onClick={toggleFlip}>
        {/* FRENTE */}
        <div className="flip-card-front">
          {item.image ? (
            <img src={item.image} alt={item.name} className="item-image" />
          ) : (
            <div className="item-image-placeholder">
              📷 Toca para ver descripción
            </div>
          )}
          <div className="item-front-info">
            <h4 className="item-name">{item.name}</h4>
            <span className="item-price-preview">
              ${item.price.toFixed(2)}
            </span>
          </div>
        </div>

        {/* REVERSO */}
        <div className="flip-card-back">
          <div className="back-content">
            <h4 className="item-name-back">{item.name}</h4>
            <p className="item-description">{item.description}</p>
            
            {item.hasVariants && !localOnly && (
              <div className="item-variants" onClick={(e) => e.stopPropagation()}>
                <label>Selecciona:</label>
                <select 
                  value={selectedVariant}
                  onChange={(e) => {
                    e.stopPropagation();
                    setSelectedVariant(parseInt(e.target.value));
                  }}
                >
                  {item.variants.map((variant, index) => (
                    <option key={index} value={index}>
                      {variant.label} {variant.price > 0 ? `(+$${variant.price.toFixed(2)})` : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className={`item-footer-back ${localOnly ? 'local-only' : ''}`}>
              <span className="item-price">
                ${item.hasVariants && selectedVariant !== undefined && !localOnly
                  ? (item.price + item.variants[selectedVariant].price).toFixed(2)
                  : item.price.toFixed(2)
                }
              </span>
              {localOnly ? (
                <div className="local-only-badge">
                  🏠 Solo en el local
                </div>
              ) : (
                <button 
                  className="add-to-cart-button" 
                  onClick={handleAddToCart}
                  data-testid={`add-to-cart-${item.id}`}
                >
                  Añadir al Carrito
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
